'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import HeroPrimary from '@src/components/Shared/HeroPrimary';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import NextImage from '@src/components/Shared/NextImage';

import HotSpots from '@src/components/Sections/HotSpots';
import AthleteTech from '@src/components/Sections/AthleteTech';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import ImageTextPrimary from '@src/components/Sections/ImageTextPrimary';
import Testimonials from '@src/components/Sections/Testimonials';
import SolutionQuote from '@src/components/Sections/SolutionQuote';
import MultiQuotes from '@src/components/Sections/MultiQuotes';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { ApplicationsClientPropTypes } from './ApplicationsPage.types';

const ApplicationsPageClient: React.FC<ApplicationsClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/applications`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <HeroPrimary
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#050505]'
        attr='hero-component'
        className='bg-background-primary! mb-[8rem] flex flex-col items-center justify-between overflow-visible! pt-[6rem] pb-[3rem] md:flex-row md:px-[4rem]! md:pt-[9.3rem] md:pb-[6.3rem] lg:gap-[2rem] lg:pt-[11.2rem] lg:pb-[8.2rem] 2xl:h-[75rem]! 2xl:py-[9.6rem]'
        title={page?.hero?.items?.[0]?.title}
        bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
        image={page?.hero?.items?.[0]?.mediaPrimary}
        titleClassName='text-[4rem] lg:text-[5.7rem] 2xl:tracking-[-0.2432rem] leading-[4.7rem] lg:leading-[6.9rem] md:max-w-[36.6rem] md:max-w-3xl lg:max-w-[50rem] 2xl:max-w-4xl 2xl:tracking-[-0.152rem]'
        imgClassName='overflow-hidden scale-[1.2] sm:scale-none [&>img]:relative! [&>img]:bottom-[3.6rem] md:[&>img]:bottom-[unset] absolute! sm:relative! sm:overflow-visible! flex items-end md:items-center justify-end xl:max-h-[80%] top-[0] h-full md:w-[50%] opacity-[0.2] sm:opacity-[1] [&>img]:w-auto! md:[&>img]:max-w-[160%]! md:[&>img]:right-[-35%] md:[&>img]:absolute!'
        bodyCopyClassName='[&>p]:text-[1.6rem] md:[&>p]:text-[1.8rem] lg:[&>p]:text-[2rem] 2xl:[&>p]:text-[2rem] [&>p]:leading-[150%] md:max-w-[34.2rem] md:max-w-[43.7rem] lg:max-w-[51.7rem] 2xl:max-w-[59rem] relative md:top-[0] mt-[1.6rem]'
        childrenClassName='xsm:max-w-full'
      />

      <Container
        attr='full-feature-component'
        id={page?.valdHub?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0]! md:flex md:flex-col'
      >
        <SectionHeader
          className='mb-[3.4rem] justify-center lg:pb-[unset]'
          pretitle={page?.valdHub?.items?.[0]?.preTitle}
          title={page?.valdHub?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[3.2rem] xl:leading-[5.4rem] font-light 2xl:max-w-[100rem] '
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.valdHub?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.valdHub?.items?.[0]?.buttonCollection?.items} className='justify-center' />
          </>
        </SectionHeader>

        <HotSpots className='mb-[8rem] p-[0]! md:mb-[14rem]' data={page?.valdHub?.items?.[0]} />
      </Container>

      <AthleteTech
        id={page?.athleteTech?.items?.[0]?.scrollTo?.slice(1)}
        items={page?.athleteTech?.items?.[0]?.topicCollection?.items}
      />

      <Container
        attr='full-feature-component'
        id={page?.everyPerformance?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0]! md:flex md:flex-col'
      >
        <SectionHeader
          className='mb-[3.4rem] justify-center lg:pb-[unset]'
          pretitle={page?.everyPerformance?.items?.[0]?.preTitle}
          title={page?.everyPerformance?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5.4rem] font-light 2xl:max-w-[100rem] '
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.everyPerformance?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        </SectionHeader>
        <NextImage
          className='order-0 mx-auto mb-[2.4rem] w-full max-w-[90%] md:mt-[2.4rem] md:mb-[0rem] lg:max-w-[70%]'
          src={page?.everyPerformance?.items?.[0]?.mediaPrimary.url}
          alt='A portrait'
          objectFit='contain'
          aspectRatio={
            page?.everyPerformance?.items?.[0]?.mediaPrimary.width /
            page?.everyPerformance?.items?.[0]?.mediaPrimary?.url?.height
          }
          width={page?.everyPerformance?.items?.[0]?.mediaPrimary.width}
        />
      </Container>

      <ImageTextSecondary
        id={page?.dataInformed?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.dataInformed?.items?.[0]?.title}
        imgURL={page?.dataInformed?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.dataInformed?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />
      <ImageTextSecondary
        reversed
        id={page?.betterManagement?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.betterManagement?.items?.[0]?.title}
        imgURL={page?.betterManagement?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.betterManagement?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.betterManagement?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextPrimary
        id={page?.measurement?.items?.[0]?.scrollTo?.slice(1)}
        className='lg:items-center'
        pretitle={page?.measurement?.items?.[0]?.preTitle}
        title={page?.measurement?.items?.[0]?.title}
        imgURL={page?.measurement?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.measurement?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.measurement?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.normativeData?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.normativeData?.items?.[0]?.preTitle}
        title={page?.normativeData?.items?.[0]?.title}
        imgURL={page?.normativeData?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.normativeData?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.normativeData?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <Container attr='solution-header-component' id={page?.integrations?.items?.[0]?.scrollTo?.slice(1)}>
        <SectionHeader
          className='mb-[3.2rem]'
          pretitle={page?.integrations?.items?.[0]?.preTitle}
          title={page?.integrations?.items?.[0]?.title}
          titleClassName='text-[2.2rem] max-w-[120rem] leading-[2.6rem] mb-[1.8rem]! md:leading-[3.6rem]! md:text-[2.8rem] md:leading-[7.4rem]! lg:text-[4.8rem] lg:tracking-[-0.096rem] lg:mb-[3.2rem] 2xl:text-[7.2rem] 2xl:tracking-[-0.072rem] 2xl:mb-[3.9rem]!'
          childrenClassName='text-[1.6rem] leading-[2.2rem] md:leading-[2.4rem] lg:max-w-[74.6rem]'
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.integrations?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons className='justify-center' items={page?.integrations?.items?.[0]?.buttonCollection?.items} />
          </>
        </SectionHeader>

        <NextImage
          className='mx-auto max-w-[100rem]'
          src={page?.integrations?.items?.[0]?.logo?.url}
          alt={page?.integrations?.items?.[0]?.logo?.description ?? 'VALD Image'}
          aspectRatio={page?.integrations?.items?.[0]?.logo?.width / page?.integrations?.items?.[0]?.logo?.height}
          width={page?.integrations?.items?.[0]?.logo?.width} // Set actual width
        />
      </Container>

      <ImageTextSecondary
        reversed
        id={page?.analysis?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-primary'
        className='overflow-y-visible py-[4.2rem]'
        title={page?.analysis?.items?.[0]?.title}
        titleClassName='text-body-copy--light lg:max-w-[45rem]'
        imgURL={page?.analysis?.items?.[0]?.mediaPrimary?.url}
        imgFirstClassName='order-10! lg:-order-1'
        imgSecondClassName='lg:w-[80%]'
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.analysis?.items?.[0]?.bodyCopy?.json?.content}
              className='text-body-copy--light lg:max-w-[45rem]'
            />
          </>
        }
      />

      <SolutionQuote testimonial={page?.testimonial?.items?.[0]} />

      <MultiQuotes
        id={page?.moreTestimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.moreTestimonials?.items?.[0]?.topicCollection?.items}
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default ApplicationsPageClient;
